/**
 * Bending Spoons SDK (Orion experiments) wiring for homepage-webapp. Mirrors the
 * listings-webapp pattern documented in `docs/orion-feature-flags.md`:
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsLinearScoreEnabled`).
 *
 * homepage-webapp otherwise ramps via Statsig; Orion is introduced here so the
 * linear-ranker paid-boost experiment shares a single `enable_linear_score`
 * switch with the eb-ui discover surfaces and listings-webapp related events.
 *
 * To add a new flag: append one key + one default to `HomepageWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';

export type HomepageWebappSettings = {
    enable_linear_score: boolean;
};

const DEFAULT_SETTINGS = createAppDefaults<HomepageWebappSettings>({
    enable_linear_score: false,
});

export const useBspConfig = () =>
    useMemo(() => {
        const domain = typeof window !== 'undefined' ? window.location.hostname : '';
        const environment = domain.includes('evbqa')
            ? 'staging'
            : domain.includes('localhost')
              ? 'development'
              : 'production';

        return {
            appName: 'homepage-webapp',
            environment,
            defaultOrionSettings: DEFAULT_SETTINGS,
            defaultSegmentations: {},
        } as const;
    }, []);

/**
 * Gate for the linear-ranker paid-boost experiment (`enable_linear_score` Orion
 * flag). When on, the homepage browse search + popular-events carousel ask the
 * search service to rank with the linear ranker (which carries the continuous
 * paid-boost price signal) via `score_mode: 'linear'`, instead of default
 * multiplicative scoring.
 *
 * Same `enable_linear_score` key as the eb-ui discover surfaces
 * (`useIsLinearScoreEnabled`) and listings-webapp related events, so every
 * surface ramps in lockstep off a single switch. Staff-gated server-side, so
 * inert for non-staff until switched on.
 *
 * Returns `isLoading` so callers can hold their fetch until Orion settles —
 * otherwise a treatment visitor whose Orion resolves after the fetch fires
 * would be read as control and silently get the default ranker.
 */
export const useIsLinearScoreEnabled = (): { isEnabled: boolean; isLoading: boolean } => {
    let settings: Partial<HomepageWebappSettings> = {};
    let isLoading = false;
    try {
        const state = useOrionSettings<HomepageWebappSettings>();
        settings = state.settings;
        isLoading = state.isLoading;
    } catch {
        // Swallow — useOrionSettings throws on the SSR render path (no BspProvider).
    }
    return { isEnabled: settings.enable_linear_score ?? false, isLoading };
};
